// User administration (D51, D52): everyone who can sign in with their role; adding a user
// with a temporary password or an invitation link; changing a role, resetting a password
// and disabling a user (both end that user's sessions), and issuing the one-time code a user
// with no active device links their first device with. Each control shows only with its
// permission; the API checks them all. There is no sign-up. Layout from
// design/isomorphic/src/pages/users.tsx, with a count row from its executive stats.
import { Plus, ShieldStar, UsersThree } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import type { User } from "../api/client";
import { accessQuery, can, type Me } from "../auth/access";
import { inviteUser, type Invitation, rolesQuery, setUserDisabled, setUserRole, usersQuery } from "../auth/admin";
import { type DeviceCode, issueDeviceCode } from "../device/codes";
import { useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { AddUserDialog, DeviceCodeDialog, InvitationDialog, ResetPasswordDialog } from "./UsersDialogs";
import { type Actions, UserCounts, UsersTable } from "./UsersTable";

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function ManageUsers({ me }: { me: Me }) {
  const t = useT().users.list;
  const crumbs = sectionCrumbs("users");
  const query = useQuery(usersQuery());
  const roles = useQuery({ ...rolesQuery(), enabled: can(me, "ViewRoles") });
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<User>();
  const [invitation, setInvitation] = useState<{ user: User; link: Invitation }>();
  const [issued, setIssued] = useState<{ user: User; code: DeviceCode }>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  async function act(work: () => Promise<unknown>, done: string) {
    try {
      await work();
      toast(done);
      await refresh();
    } catch (e) {
      toast(message(e), "warn");
    }
  }

  async function invite(user: User) {
    try {
      setInvitation({ user, link: await inviteUser(user.id) });
    } catch (e) {
      toast(message(e), "warn");
    }
  }

  async function deviceCode(user: User) {
    try {
      setIssued({ user, code: await issueDeviceCode(user.id) });
    } catch (e) {
      toast(message(e), "warn");
    }
  }

  const actions: Actions = {
    me,
    roles: roles.data ?? [],
    toggle: (u) => {
      const disable = u.disabled_at == null;
      void act(() => setUserDisabled(u.id, disable), disable ? t.wasDisabled(u.email) : t.wasEnabled(u.email));
    },
    reset: setResetting,
    invite: (u) => void invite(u),
    deviceCode: (u) => void deviceCode(u),
    assign: (u, roleId) => void act(() => setUserRole(u.id, roleId), t.roleChanged(u.email)),
  };

  return (
    <>
      <PageHeader
        title={SECTIONS.users.title}
        breadcrumb={crumbs}
        description={t.description}
      >
        {can(me, "InviteUser") ? (
          <Button className="gap-1.5" onClick={() => setAdding(true)}>
            <Plus weight="bold" className="h-4 w-4" /> {t.add}
          </Button>
        ) : null}
      </PageHeader>
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState message={query.error.message} /> : null}
      {query.data?.length === 0 ? (
        <EmptyState icon={<UsersThree weight="duotone" />} title={t.empty}>
          {t.emptyBody}
        </EmptyState>
      ) : null}
      {query.data && query.data.length > 0 ? <UserCounts users={query.data} /> : null}
      {query.data && query.data.length > 0 ? (
        <div className={cn(cardClass, "p-3 lg:p-4")}>
          <UsersTable users={query.data} actions={actions} />
        </div>
      ) : null}
      <AddUserDialog
        open={adding}
        roles={roles.data ?? []}
        onClose={() => setAdding(false)}
        onAdded={async (user, link) => {
          await refresh();
          if (link) setInvitation({ user, link });
        }}
      />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(undefined)} />
      <InvitationDialog invitation={invitation} onClose={() => setInvitation(undefined)} />
      <DeviceCodeDialog issued={issued} onClose={() => setIssued(undefined)} />
    </>
  );
}

export default function UsersPage() {
  const t = useT().users.list;
  const crumbs = sectionCrumbs("users");
  const me = useQuery(accessQuery());
  if (me.isPending) return <Loading />;
  if (!can(me.data, "ViewUsers")) {
    return (
      <>
        <PageHeader title={SECTIONS.users.title} breadcrumb={crumbs} />
        <EmptyState icon={<ShieldStar weight="duotone" />} title={t.cannotSee}>
          {t.cannotSeeBody}
        </EmptyState>
      </>
    );
  }
  return <ManageUsers me={me.data!} />;
}
